import '@workspace/ui/globals.css';
import '~/styles/common.scss';

import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { company } from '~/company/Company';
import { APPLE_ICONS, getFaviconDir } from '~/company/favicons';
import { ModalStockChangedV3 } from '~/components/ModalStockChangedV3';
import { ModalUpdateRequired } from '~/components/ModalUpdateRequired';
import type { IntercomType } from '~/hooks/useIntercom';
import { useIsRN } from '~/hooks/useIsRN';
import { useMounted } from '~/hooks/useMounted';
import { StoresProvider } from '~/hooks/useStore';

import type {
  CategoriesResponse,
  CategoryListAPI,
  ProductAPI,
  ProductsPagedResponse,
} from './api/Catalog';
import type { ReviewsResponse } from './api/Customer';
import { KYCStatus } from './api/KYCStatus';
import { BREAKPOINTS } from './breakpoints';
import Alerts from './components/Alert/Alert';
import CookieBanner from './components/CookieBanner';
import { ModalType } from './components/Modal/interface';
import { deliveryAddressStore } from './components/ModalDeliveryAddress/DeliveryAddressStore';
import ModalList from './components/ModalList/ModalList';
import NotificationContainer from './components/NotificationContainer/NotificationContainer';
import PromocodeNotifications from './components/PromocodeNotifications/PromocodeNotifications';
import SideBarChooseCompany from './components/SideBarChooseCompany/SideBarChooseCompany';
import GlobalHooks from './hooks/GlobalHooks';
import { GlobalContext } from './hooks/useGlobal';
import { useLang } from './hooks/useLang';
import useMediaQuery from './hooks/useMediaQuery';
import { useModal } from './hooks/useModal';
import { useNavigationAfterVerification } from './hooks/useNavigationAfterVerification';
import { useNavigationTracking } from './hooks/useNavigationTracking';
import { usePickupStoreLink } from './hooks/usePickupStoreLink';
import useSommelierButtonScript from './hooks/useSommelierButtonScript';
import { useStoreRedirect } from './hooks/useStoreRedirect';
import Complete from './pages/Complete/Complete';
import OnboardingFactory from './pages/OnboardingV3';
import Root from './pages/Root/Root';
import { pickupStoreLinkState } from './services/pickupStoreLinkState';
import { mainStore, userStore } from './stores';
import type { ISnap } from './stores/interfaces/Midtrans';
import {
  getIsMandatoryAddressSelection,
  getMandatoryAddressModalType,
  isMandatoryAddressFlowModal,
} from './utils/mandatoryAddress';
import { isVersionGreaterOrEqual } from './utils/platform';

import 'react-loading-skeleton/dist/skeleton.css';

const MIN_SUPPORTED_VERSION = '1.0.41';

declare global {
  interface Window {
    fbq: (track: string, event: string, data?: Record<string, unknown>) => void;
    /**
     * Midtrans Snap. Absent until its SDK arrives — the tag is deferred past
     * the first paint, so checkout has to go through `__loadSnap` rather than
     * assume it is there.
     */
    snap?: ISnap;
    /** Loads Snap on demand and resolves once `snap` is usable. */
    __loadSnap?: () => Promise<void>;
    /**
     * Identifies the build this document was rendered from. Compared against
     * the served `/buildHash.txt` to tell a deploy apart from a network failure
     * when a chunk does not load. Absent in dev, where nothing is hashed.
     */
    __BUILD_HASH__?: string;
    __INITIAL_STATE__: {
      context: GlobalContext;
      product?: ProductAPI;
      reviews?: ReviewsResponse;
      catalogCategory?: ProductsPagedResponse;
      homeCategories?: CategoriesResponse;
      shopCategories: CategoryListAPI[];
      heroBanners?: CategoriesResponse;
      /**
       * Set to true when the SSR preload phase failed (5xx). Client-side
       * stores use it to force a refetch on hydration instead of rendering
       * an empty page served from the degraded shell.
       */
      ssrDegraded?: boolean;
    };
    Intercom: IntercomType;
    intercomSettings: {
      app_id: string;
      [key: string]: any;
    };
  }
}

interface AppProps {
  globalContext?: Partial<GlobalContext>;
  serverLang?: string;
}

const App = ({ globalContext = {}, serverLang }: AppProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { openModal } = useModal();
  const [searchParams] = useSearchParams();
  const { search } = location;
  const { i18n } = useTranslation();
  const isRN = useIsRN();
  const mounted = useMounted();
  useLang();
  useNavigationTracking();
  // Before the address gate below: it must see the suppression this sets.
  usePickupStoreLink();

  useEffect(() => {
    if (deliveryAddressStore.isOutOfZoneModalOpen) {
      openModal(ModalType.OutOfZone);
    }
  }, [deliveryAddressStore.isOutOfZoneModalOpen]);

  useEffect(() => {
    const isBlockedRoute = ['/legals', '/onboarding'].includes(
      location.pathname,
    );

    if (
      getIsMandatoryAddressSelection() &&
      !isBlockedRoute &&
      !isMandatoryAddressFlowModal(searchParams.get('modal'))
    ) {
      openModal(
        getMandatoryAddressModalType(),
        undefined,
        undefined,
        undefined,
        true,
      );
    }
  }, [
    location.pathname,
    searchParams,
    userStore.deliveryAddress,
    mainStore.isAllStoresSynchronized,
    mainStore.isRN,
    mainStore.onboardingSeen,
    pickupStoreLinkState.isPending,
  ]);

  const global: GlobalContext = {
    ...globalContext,
    isSSR: typeof window === 'undefined',
    isMobile: useMediaQuery(
      `(max-width: ${BREAKPOINTS.sm}px)`,
      !!globalContext.isMobile,
    ),
    isTablet: useMediaQuery(
      `(max-width: ${BREAKPOINTS.tablet}px)`,
      !!globalContext.isTablet,
    ),
  };

  if (serverLang) {
    i18n.changeLanguage(serverLang);
  }

  useNavigationAfterVerification();
  useStoreRedirect();
  useSommelierButtonScript({
    isMobile: globalContext?.isMobile,
    isTablet: globalContext?.isTablet,
  });

  useEffect(() => {
    //TODO: remove in 2-3 moths
    mainStore.sendToRN('removeTag', 'warehouse_id');

    if (
      !mainStore.onboardingSeen &&
      mainStore.isRN &&
      company.hasOnboarding &&
      location.pathname !== '/legals'
    ) {
      navigate('/onboarding', { replace: true });
    } else {
      if (!company.newDesignSystemEnabled) {
        mainStore.sendToRN('setStatusBar', 'dark-content');
      }
    }
  }, [
    mainStore.onboardingSeen,
    userStore.personalData.isAdult,
    location.pathname,
  ]);

  useEffect(() => {
    if (!search || import.meta.env.REACT_APP_ENV === 'production') {
      return;
    }

    // window.history.back();
  }, [search]);

  // Android WebView does not expose env(safe-area-inset-*). Ask RN for the
  // real insets on start-up and store them as CSS variables used as a
  // fallback by --safe-area-inset-top/--safe-area-inset-bottom.
  useEffect(() => {
    if (!mainStore.isRN) return;

    mainStore.sendToRN('getSafeAreaInsets', null, (data) => {
      const topPx = Number(data?.top);
      const bottomPx = Number(data?.bottom);

      if (Number.isFinite(topPx)) {
        document.documentElement.style.setProperty(
          '--safe-area-inset-top-fallback',
          `${topPx}px`,
        );
      }

      if (Number.isFinite(bottomPx)) {
        document.documentElement.style.setProperty(
          '--safe-area-inset-bottom-fallback',
          `${bottomPx}px`,
        );
      }
    });
  }, []);

  // On the server and the first client render the persisted stores are not yet
  // hydrated, and SSR always emits the full tree. Blocking here on the client's
  // first render (before mount) would render an empty tree against the
  // server-rendered HTML and crash hydration. Apply the sync gate only after
  // mount, by which point the (async) store hydration has resolved.
  if (mounted && !mainStore.isAllStoresSynchronized) {
    return <></>;
  }

  const appleIcons = APPLE_ICONS[company.name];
  const isIndexablePage =
    location.pathname.startsWith('/c/') || location.pathname.startsWith('/p/');

  return (
    <GlobalContext.Provider value={global}>
      <StoresProvider>
        <GlobalHooks />
        <Alerts />
        <NotificationContainer />
        <CookieBanner />
        <div
          dir={userStore.dir}
          // Hidden rather than unmounted. Unmounting takes Root's
          // useOnWarehouseChange refs with it, and those refs are what make a
          // warehouse change detectable: on a fresh mount the previous code is
          // undefined, so `isChanged` is false, and Root's guards
          // (`isChanged || !list.length`) see the SSR-preloaded lists of the
          // default warehouse and skip the refetch. The customer would get the
          // right cart and ETA but the wrong shop's categories, catalog tree
          // and hero banner — the one thing this link exists to prevent.
          style={
            pickupStoreLinkState.isBlocking
              ? { visibility: 'hidden' }
              : undefined
          }
        >
          <Helmet>
            <title>{company.config.name}</title>
            <meta name="description" content={company.config.name} />
            <meta
              data-react-helmet="true"
              name="robots"
              content={isIndexablePage ? 'index, follow' : 'noindex, nofollow'}
            />
            {company.isEnabledIndexNow && (
              <meta
                name="msvalidate.01"
                content={
                  import.meta.env.INDEXNOW_KEY ||
                  '99c601bc3999477ca8484b82b5e83496'
                }
              />
            )}
            <link
              data-react-helmet="true"
              rel="canonical"
              href={company.config.canonical + (location.pathname ?? '')}
            />
            {/* SSR favicon so search-engine crawlers (which don't run JS) can
                find it in the raw HTML; mirrors the client-side path. */}
            <link
              data-react-helmet="true"
              rel="icon"
              sizes="any"
              href={`/${getFaviconDir(company.name)}/favicon.ico`}
            />
            {appleIcons && (
              <link
                data-react-helmet="true"
                rel="apple-touch-icon"
                sizes={appleIcons.appleTouchIconSizes}
                href={appleIcons.appleTouchIcon}
              />
            )}
          </Helmet>
          {import.meta.env.REACT_APP_ENV === 'production' ? null : (
            <SideBarChooseCompany />
          )}
          <Routes>
            {company.hasOnboarding && isRN && (
              <Route path="/onboarding" element={<OnboardingFactory />} />
            )}
            <Route path="/complete" element={<Complete />} />
            <Route path="*" element={<Root />} />
          </Routes>
          <ModalList />
          {/* This modal placed here 'cuz it should open independent of the `modal` query param */}
          <ModalStockChangedV3 />
          {/* Blocking modal for outdated app version - only for RN and CityDrinks */}
          {company.isUpdateRequiredModalEnabled &&
            isRN &&
            mainStore.appVersion &&
            !isVersionGreaterOrEqual(
              mainStore.appVersion,
              MIN_SUPPORTED_VERSION,
            ) && <ModalUpdateRequired />}
          {mounted &&
            userStore.isAuthorized &&
            userStore.personalData.kycStatus === KYCStatus.Verified && (
              <PromocodeNotifications />
            )}
        </div>
      </StoresProvider>
    </GlobalContext.Provider>
  );
};

export default observer(App);
